import { z } from 'zod';
import { ReservationStatusSchema } from './dashboard.js';
import {
  GetMediaUnitsAvailabilityRequestSchema,
  GetMediaUnitsAvailabilityResponseSchema,
} from './availability.js';
import {
  ReservationListResponseSchema,
  UnitReservationQuerySchema,
  AuditEventListQuerySchema,
  AuditEventListResponseSchema,
} from './reservation-list.js';

// Queue name constant shared between api and worker packages.
export const TRASH_CLEANUP_QUEUE_NAME = 'trash-cleanup';
import { createPaginatedResponseSchema } from './pagination.js';
import { endpoint } from '../api/endpoint.js';
import { MediaTypeEnum, MediaUnitVariantSchema } from './media-unit-variants.js';
import { BundleDetailSchema } from './bundles.js';
import {
  sharedMediaUnitFilterFields,
  filterArrayField,
  validateSharedMediaUnitFilterRefinements,
} from './media-unit-filter-fields.js';
import { AttachmentSchema } from './attachments.js';
import { TagDefinitionSchema } from './tag-definition.js';

// ─── Media unit list item ─────────────────────────────────────

export const MediaUnitListItemSchema = z.object({
  id: z.number(),
  grwId: z.string(),
  supplierNumber: z.string().nullable(),
  formatCode: z.string().nullable(),
  /** Non-null when formatId is set since media_formats.label is NOT NULL */
  formatLabel: z.string().nullable(),
  supplierName: z.string().nullable(),
  /** Non-null since location_id is NOT NULL */
  city: z.string(),
  address: z.string().nullable(),
  region: z.string().nullable(),
  county: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  widthM: z.number().nullable(),
  heightM: z.number().nullable(),
  areaSqm: z.number().nullable(),
  material: z.string().nullable(),
  status: ReservationStatusSchema,
  catalogPrice: z.number().nullable(),
  rent: z.number().nullable(),
  exposureCost: z.number().nullable(),
  additionalCosts: z.number().nullable(),
  notes: z.string().nullable(),
  /** Internal-only contact person for WF/mural installs (from Osoba do kontaktu przy WF i muralach) */
  wfContactPerson: z.string().nullable(),
  /** Client-visible location description (from Opis lokalizacji) */
  locationDescription: z.string().nullable(),
  /** Derived server-side: true only for lit lighting values ('Tak', 'Tak (solarne)') */
  illuminated: z.boolean(),
  /** Sourced from variants — null when unit has no variants */
  mediaType: MediaTypeEnum.nullable(),
  /** Viewer-filtered tag definitions ordered by sort_order ASC NULLS LAST, label ASC (REQ-2.22) */
  tags: z.array(TagDefinitionSchema),
  /** Signed URL of the first photo (by id ASC) — null when the unit has no photos */
  firstPhotoUrl: z.string().nullable(),
  // ─── New fields (task 1.1) ────────────────────────────────
  /** From locations.agglomeration */
  agglomeration: z.string().nullable(),
  /** From media_units.audience_estimate */
  audienceEstimate: z.number().nullable(),
  /** From media_units.dimension_notes */
  dimensionNotes: z.string().nullable(),
  /** From media_units.multiformat */
  multiformat: z.string().nullable(),
  /** From suppliers.contact_info */
  supplierContactInfo: z.string().nullable(),
  /** From suppliers.email */
  supplierEmail: z.string().nullable(),
  /** From suppliers.phone */
  supplierPhone: z.string().nullable(),
  /**
   * Road number resolved from the unit's primary variant (lowest variant id) —
   * catalog-column-filter-parity reverse-parity column for the `roadNumber`
   * filter. Null when the unit has no variants or the primary variant has no
   * road_number. See packages/db's primaryVariantRoadNumberExpr.
   */
  roadNumber: z.string().nullable(),
  /** Discounted price from cost record */
  discountedPrice: z.number().nullable(),
  /** Last-minute price from cost record */
  lastMinutePrice: z.number().nullable(),
  /** Starter price from cost record */
  starterPrice: z.number().nullable(),
  /** Production cost from cost record */
  productionCost: z.number().nullable(),
  /** Installation cost from cost record */
  installationCost: z.number().nullable(),
  /** Dismantling cost from cost record */
  dismantlingCost: z.number().nullable(),
  /** Client production price from cost record */
  clientProduction: z.number().nullable(),
  /** Client installation price from cost record */
  clientInstallation: z.number().nullable(),
  /** Client dismantling price from cost record */
  clientDismantling: z.number().nullable(),
  /** Client additional fees from cost record */
  clientAdditionalFees: z.number().nullable(),
  /**
   * True when this row's internal cost values were nulled by the server's
   * field-visibility gate rather than genuinely absent (task 1.1,
   * CU-86cbc3r8b) — carried on the row itself so a caller whose grant covers
   * only some media types can distinguish withheld from absent per row. See
   * field-visibility's "Restricted values are masked, not removed from the
   * layout". Present identically on OfferUnitListItemSchema (offer-unit.ts);
   * flows to MediaUnitDetailSchema for free via its `.extend()` below.
   *
   * Resolved INDEPENDENTLY of `supplierWithheld` below (field-visibility spec:
   * "Permission to see costs and permission to see supplier details SHALL be
   * resolved independently of one another") — a caller who may see costs but
   * not supplier details must never get this flipped true by the supplier
   * denial, or a genuinely-null cost value would wrongly render as withheld.
   *
   * `.default(false)` per the "Rollout and rollback" Decision (proposal.md):
   * an old, pre-deploy response omits this key entirely during the api-before-
   * web rollback window, and its absence MUST read as "not withheld" (false),
   * never as "values were withheld". `.default()` keeps the key optional on
   * the parse input while guaranteeing the parsed output is always boolean.
   */
  costsWithheld: z.boolean().default(false),
  /**
   * True when this row's `rent` value specifically was nulled by the
   * server's field-visibility gate (rent-cost-visibility-permission,
   * CU-86cbgnt3z) — independent of `costsWithheld` above, since a caller may
   * hold `cost.view` but lack the additional wildcard `cost.view_rent`
   * grant, in which case every other cost is visible and only rent is
   * masked. Whenever `costsWithheld` is true, this SHALL also read true, so
   * no combination of the two flags produces an unmasked-looking rent cell
   * for a caller who cannot see any of the row's costs at all. Present
   * identically on OfferUnitListItemSchema (offer-unit.ts); flows to
   * MediaUnitDetailSchema for free via its `.extend()` below.
   *
   * `.default(false)` for the same pre-deploy-response rollback-window
   * reason as `costsWithheld`.
   */
  rentWithheld: z.boolean().default(false),
  /**
   * True when this row's supplier-identifying values (`supplierName`/
   * `supplierNumber`) were nulled by the server's field-visibility gate
   * rather than genuinely absent — the supplier-dimension sibling of
   * `costsWithheld` above; see that field's doc for the independence
   * requirement and the rollback-window `.default(false)` rationale.
   */
  supplierWithheld: z.boolean().default(false),
});

export type MediaUnitListItem = z.infer<typeof MediaUnitListItemSchema>;

// ─── Query params ─────────────────────────────────────────────

/**
 * Closed whitelist of sortable column names.
 * Never interpolated directly as string into SQL — mapped to Drizzle column refs.
 */
export const MEDIA_UNIT_SORT_KEYS = [
  'id',
  'grwId',
  'supplierNumber',
  'city',
  'address',
  'region',
  'status',
  'areaSqm',
  'widthM',
  'heightM',
  // ─── Extended sort keys (CU-86cb1nqc0) ───
  /** Composite: ORDER BY widthM then heightM (fixed tiebreak) — see SORT_COLUMNS in list-media-units.ts. */
  'dimensions',
  'mediaType',
  'supplierName',
  'agglomeration',
  'county',
  'audienceEstimate',
  'illuminated',
  // Cost-record fields — must match COST_RANGE_DIMENSIONS ids (media-unit-cost-range-dimensions.ts).
  // The API layer guards sortBy/sortBy2 for the internal-cost subset against
  // callers without an unscoped cost.view grant (see
  // guardSortForFieldVisibility, apps/api's media-units.service.ts) — mirrors
  // the existing cost range-filter guard. cost.view gates internal costs
  // only, never client prices — see HIDDEN_COST_FIELD_IDS
  // (apps/api/src/common/field-visibility.ts) for the current split.
  'catalogPrice',
  'rent',
  'exposureCost',
  'additionalCosts',
  'discountedPrice',
  'lastMinutePrice',
  'starterPrice',
  'productionCost',
  'installationCost',
  'dismantlingCost',
  'clientProduction',
  'clientInstallation',
  'clientDismantling',
  'clientAdditionalFees',
  // ─── Reverse-parity + free-text/multi-value sort keys (CU-86cb1nqc0 follow-up) ───
  // Plain scalar text columns on media_units — trivial ORDER BY.
  'material',
  'multiformat',
  'notes',
  // Many-to-one FK join (mediaUnits.formatId -> mediaFormats.id), same shape as supplierName.
  'format',
  // Derived from the primary variant (lowest id), same resolver as mediaType's display projection.
  'roadNumber',
  // Per-unit alphabetically-sorted, comma-delimited string of active tag labels (unitTagsAggExpr).
  'tags',
] as const;

export type MediaUnitSortKey = (typeof MEDIA_UNIT_SORT_KEYS)[number];

/**
 * Multi-value dimension fields that support exclusion (negation) mode.
 * A field name listed in `filters.exclude` negates that field's
 * `filters[<field>][]` array (NOT-in instead of in); absent ⇒ include.
 *
 * The four location dimension fields (`region`, `county`, `agglomeration`,
 * `city`) do NOT support exclusion mode — they combine with OR semantics
 * among themselves instead (see the "Location filters combine with OR
 * semantics" requirement in the media-unit-filter-api spec).
 */
export const EXCLUDABLE_FILTER_FIELDS = ['supplierId', 'mediaType', 'formatCode'] as const;

export type ExcludableFilterField = (typeof EXCLUDABLE_FILTER_FIELDS)[number];

/**
 * Reusable filter shape for media unit queries.
 * Each field is optional; if omitted that dimension is not filtered.
 * roadNumber lives on media_unit_variants.road_number (not on media_units) —
 * it is resolved through an EXISTS subquery over the unit's variants
 * (packages/db build-filter-conditions.ts).
 *
 * Most fields — including the numeric range dimensions (area/width/height/
 * audience/cost), material/multiformat/tags/address/notes/roadNumber,
 * noPhoto, and availabilityFrom/To — are shared with MapPinsQuerySchema via
 * sharedMediaUnitFilterFields and intentionally widen the map contract
 * (map-catalog-filter-parity). `status`, `importJobId`, and `exclude` stay
 * catalog-only here — `status` because the two schemas use different Zod
 * types for it, `importJobId`/`exclude` because they have no map-pins
 * equivalent. `search` is declared independently on each schema (identical
 * shape) since MapPinsQuerySchema already has its own `search` field.
 */
export const MediaUnitFiltersSchema = z
  .object({
    ...sharedMediaUnitFilterFields,
    status: filterArrayField.optional(),
    /**
     * Catalog-only: when present, restrict to units associated with the given import job.
     * Positive integer — corresponds to import_jobs.id. Coerced because query params arrive as strings.
     */
    importJobId: z.coerce.number().int().positive().optional(),
    /** Multi-column free-text search term. */
    search: z.string().optional(),
    /** Sidecar list of dimension fields whose values are negated (exclude mode). */
    exclude: z.array(z.enum(EXCLUDABLE_FILTER_FIELDS)).optional(),
    // supplierEmail/supplierPhone/supplierContactInfo filters were removed
    // entirely (CU-86cb1nq7h, client request: "Powinny zostać tylko w
    // zakładce 'dostawcy'" — these fields belong only in the Suppliers area).
    // supplierNumber stays (client-confirmed key identifier, not contact info).
  })
  .superRefine(validateSharedMediaUnitFilterRefinements);

export type MediaUnitFilters = z.infer<typeof MediaUnitFiltersSchema>;

export const MediaUnitListQuerySchema = z.object({
  /** Opaque pagination cursor (offset-based). Pass the value from meta.nextCursor to fetch the next page. */
  cursor: z
    .string()
    .regex(/^\d+$/, 'cursor must be a non-negative integer string')
    .max(20)
    .optional(),
  pageSize: z.coerce.number().min(1).max(500).default(100),
  sortBy: z.enum(MEDIA_UNIT_SORT_KEYS).default('id'),
  sortDir: z.enum(['asc', 'desc']).default('asc'),
  filters: MediaUnitFiltersSchema.optional(),
  sortBy2: z.enum(MEDIA_UNIT_SORT_KEYS).optional(),
  sortDir2: z.enum(['asc', 'desc']).default('asc'),
});

export type MediaUnitListQuery = z.infer<typeof MediaUnitListQuerySchema>;

// ─── Response schema ──────────────────────────────────────────

const baseSchema = createPaginatedResponseSchema(MediaUnitListItemSchema);

export const MediaUnitListResponseSchema = baseSchema.extend({
  meta: baseSchema.shape.meta.extend({
    statusCounts: z.object({
      FREE: z.number(),
      ASK_AVAILABILITY: z.number(),
    }),
  }),
});

export type MediaUnitListResponse = z.infer<typeof MediaUnitListResponseSchema>;

// ─── Detail schemas ───────────────────────────────────────────

export const MediaUnitPhotoSchema = z.object({
  id: z.number(),
  storageKey: z.string(),
  originalName: z.string(),
  url: z.string(),
  // Server-resolved rendition URLs (media-photo-derivatives). Never null when
  // the photo exists — the API falls back to the original signed URL when a
  // rendition has not been generated yet.
  thumbnailUrl: z.string(),
  displayUrl: z.string(),
});

export type MediaUnitPhoto = z.infer<typeof MediaUnitPhotoSchema>;

export const MediaUnitDetailSchema = MediaUnitListItemSchema.extend({
  displayNumber: z.string(),
  // Internal cost fields. No `printCost` — production cost is the single
  // production-related cost on the detail shape (CU-86cbc4mjk consolidation).
  exposureCost: z.number().nullable(),
  installCost: z.number().nullable(),
  dismantlingCost: z.number().nullable(),
  productionCost: z.number().nullable(),
  additionalCosts: z.number().nullable(),
  // Client-facing price fields
  starterPrice: z.number().nullable(),
  clientProduction: z.number().nullable(),
  clientInstallation: z.number().nullable(),
  clientDismantling: z.number().nullable(),
  clientAdditionalFees: z.number().nullable(),
  muralEcoProduction: z.number().nullable(),
  // Extended fields for edit form (D-7)
  supplierId: z.number().nullable(),
  formatId: z.number().nullable(),
  lighting: z.string().nullable(),
  photos: z.array(MediaUnitPhotoSchema),
  variants: z.array(MediaUnitVariantSchema),
  bundles: z.array(BundleDetailSchema),
  attachments: z.array(AttachmentSchema),
  /** Most recent availability_windows.created_at for this unit; null when it has none. */
  availabilityLastUpdatedAt: z.string().datetime().nullable(),
});

export type MediaUnitDetail = z.infer<typeof MediaUnitDetailSchema>;

export const MediaUnitDetailResponseSchema = z.object({
  data: MediaUnitDetailSchema,
});

export type MediaUnitDetailResponse = z.infer<typeof MediaUnitDetailResponseSchema>;

// ─── Client-facing detail schema ──────────────────────────────

/**
 * Client-facing subset of MediaUnitDetailSchema. Hand-listed so its shape
 * contains ONLY client-visible keys — internal-only fields (costs, supplier,
 * notes, attachments, bundles, variants) are absent by construction. Default
 * z.object parsing strips any unknown internal keys on .parse(), making this
 * the last-line redaction guard behind server-side section omission.
 */
export const ClientMediaUnitDetailSchema = z.object({
  id: z.number(),
  displayNumber: z.string(),
  status: ReservationStatusSchema,
  city: z.string(),
  address: z.string().nullable(),
  region: z.string().nullable(),
  county: z.string().nullable(),
  /** From locations.agglomeration */
  agglomeration: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  /** Client-visible location description (from Opis lokalizacji) */
  locationDescription: z.string().nullable(),
  widthM: z.number().nullable(),
  heightM: z.number().nullable(),
  areaSqm: z.number().nullable(),
  material: z.string().nullable(),
  formatLabel: z.string().nullable(),
  mediaType: MediaTypeEnum.nullable(),
  lighting: z.string().nullable(),
  illuminated: z.boolean(),
  photos: z.array(MediaUnitPhotoSchema),
  tags: z.array(TagDefinitionSchema),
  starterPrice: z.number().nullable(),
  discountedPrice: z.number().nullable(),
  clientProduction: z.number().nullable(),
  clientInstallation: z.number().nullable(),
  clientDismantling: z.number().nullable(),
  clientAdditionalFees: z.number().nullable(),
  muralEcoProduction: z.number().nullable(),
});

export type ClientMediaUnitDetail = z.infer<typeof ClientMediaUnitDetailSchema>;

export const ClientMediaUnitDetailResponseSchema = z.object({
  data: ClientMediaUnitDetailSchema,
});

export type ClientMediaUnitDetailResponse = z.infer<typeof ClientMediaUnitDetailResponseSchema>;

// ─── Endpoint definition ──────────────────────────────────────

import { FilterOptionsResponseSchema } from './filter-options.js';

import { CostHistoryResponseSchema } from './media-units-update.js';

import { mapPinsEndpoint } from './map-pins.js';

// ─── Trash schemas ────────────────────────────────────────────

export const MediaUnitTrashItemSchema = z.object({
  id: z.number(),
  grwId: z.string(),
  city: z.string(),
  deletedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  /** Count of non-trashed offers currently referencing this unit via offer_draft_units. */
  offerReferenceCount: z.number().int().nonnegative(),
});

export type MediaUnitTrashItem = z.infer<typeof MediaUnitTrashItemSchema>;

export const MediaUnitTrashListResponseSchema = z.object({
  data: z.array(MediaUnitTrashItemSchema),
});

export type MediaUnitTrashListResponse = z.infer<typeof MediaUnitTrashListResponseSchema>;

export const MediaUnitRestoreResponseSchema = z.object({
  data: z.object({
    id: z.number(),
    grwId: z.string(),
  }),
});

export type MediaUnitRestoreResponse = z.infer<typeof MediaUnitRestoreResponseSchema>;

export const mediaUnits = {
  list: endpoint('GET', '/media-units', MediaUnitListQuerySchema, MediaUnitListResponseSchema),
  filterOptions: endpoint(
    'GET',
    '/media-units/filter-options',
    z.object({}),
    FilterOptionsResponseSchema,
  ),
  mapPins: mapPinsEndpoint,
  getById: endpoint(
    'GET',
    '/media-units/:id',
    z.object({ id: z.coerce.number().int().positive() }),
    MediaUnitDetailResponseSchema,
  ),
  getClientDetail: endpoint(
    'GET',
    '/offers/:offerId/units/:unitId/client-detail',
    z.object({
      offerId: z.coerce.number().int().positive(),
      unitId: z.coerce.number().int().positive(),
    }),
    ClientMediaUnitDetailResponseSchema,
  ),
  costHistory: endpoint(
    'GET',
    '/media-units/:id/cost-history',
    z.object({ id: z.coerce.number().int().positive() }),
    CostHistoryResponseSchema,
  ),
  deleteById: endpoint(
    'DELETE',
    '/media-units/:id',
    z.object({ id: z.coerce.number().int().positive() }),
    z.void(),
  ),
  trashList: endpoint('GET', '/media-units/trash', z.object({}), MediaUnitTrashListResponseSchema),
  restore: endpoint(
    'POST',
    '/media-units/:id/restore',
    z.object({ id: z.coerce.number().int().positive() }),
    MediaUnitRestoreResponseSchema,
  ),
  getAvailability: endpoint(
    'GET',
    '/media-units/availability',
    GetMediaUnitsAvailabilityRequestSchema,
    GetMediaUnitsAvailabilityResponseSchema,
  ),
  listReservations: endpoint(
    'GET',
    '/media-units/:id/reservations',
    z.object({ id: z.coerce.number().int().positive() }).merge(UnitReservationQuerySchema),
    ReservationListResponseSchema,
  ),
  listAuditEvents: endpoint(
    'GET',
    '/media-units/:id/audit-events',
    z.object({ id: z.coerce.number().int().positive() }).merge(AuditEventListQuerySchema),
    AuditEventListResponseSchema,
  ),
};
